import { fromBaseRecord } from './from-base-context';
import type { FromBaseRow as Row } from './from-base-context';
import { decodePhpJson } from './php-json-decode';
import { PhpFloat } from '../php-compat/ordered-array';
import { readItemField } from './item-field-reader';
import { serializeIgbinary } from './php-igbinary';
import { serializePhpJson, utf8Bytes } from './php-json-serialize';
import { phpJsonFloat } from './php-decimal-values';
import { memoFromBaseRead } from './from-base-read-memo';

export interface PlSignatureMetadata {
    encoding: 'php-serialize' | 'igbinary-v2';
    contract: string;
    codeCrc: string;
    definitionEpoch: number | null;
}
const volatileEcKeys = ['x_pos', 'y_pos', 'z_pos', 'v_current', 'v_temp', 'aps', 'aps_spent', 'reaction', 'movement_pct',
    'initiative', 'initiative_roll', 'turn_number', 'is_turn', 'turn_order', 'in_combat', 'combat_id', 'orb_queue',
    'encounter_creature_t', 'encounter_creature_t_updated', 'encounter_creature_updated_by_id', 'concentration_id', 'last_action_t'];

/** Native _ft_pl_signature_projection, at the pre-dedupe PL compile boundary. */
export function plSignatureProjection(source: Row, salt: string): Row {
    // Only these branches are edited. nativeSignatureTypes materializes a
    // detached tree before encoding; copying unrelated media/catalogs here
    // needlessly traverses them twice per signature pair.
    const row = { ...source };
    row.__pl_carry_salt = salt;
    for (const key of ['position', 'permissions', 'triggers', 'pl']) delete row[key];
    if (fromBaseRecord(row.computed)) {
        const computed = { ...row.computed };
        row.computed = computed;
        for (const key of ['_cache', 'pl', 'status_effect_permanent_pl', 'pl_meta', 'pl_pending']) delete computed[key];
        if (fromBaseRecord(computed.vitality)) {
            const vitality = { ...computed.vitality };
            computed.vitality = vitality;
            for (const key of Object.keys(vitality))
                if (/current|^v_temp$|bloodied|dying|pct|percent|state/i.test(key)) delete vitality[key];
        }
    }
    if (fromBaseRecord(row.encounter_creatures)) {
        const encounter = { ...row.encounter_creatures };
        row.encounter_creatures = encounter;
        for (const key of volatileEcKeys) delete encounter[key];
    }
    return row;
}

/** Preserve the native number types of deterministic compiler producers and the
 * memoized identity of expanded registry/DB shells before igbinary hashing.
 * These annotations never enter the served JSON. */
export interface PlSignatureJsonSources {
    calibration: ReadonlyMap<string, string>;
    vfx: ReadonlyMap<string, string>;
    staticShellKeys?: ReadonlySet<string>;
}
export function createPlSignatureJsonSources(catalog: Readonly<Record<string, Row>> | readonly [], vfxRows: readonly Row[],
    registries: Readonly<Record<string, Readonly<Record<string, Row | null>> | null>> = {}): PlSignatureJsonSources {
    const parse = (value: unknown): string | undefined => {
        if (typeof value !== 'string') return undefined;
        try { decodePhpJson(value); return value; } catch { return undefined; }
    };
    // Signature annotations depend only on immutable saved inputs, not the
    // mutable pending/served definition cache. Reuse each input independently.
    const calibration = memoFromBaseRead(catalog, 'pl-calibration-json', () => {
        const values = new Map<string, string>();
        for (const definition of Object.values(catalog)) if (Array.isArray(definition.meshes)) for (const mesh of definition.meshes as Row[]) {
            const id = readItemField(mesh, 'mesh_id'), value = parse(readItemField(mesh, 'mesh_calibration'));
            if (typeof id === 'string' && value !== undefined) values.set(id, value);
        }
        return values;
    });
    const vfx = memoFromBaseRead(vfxRows, 'pl-vfx-json', () => {
        const values = new Map<string, string>();
        for (const row of vfxRows) {
            const id = readItemField(row, 'ability_vfx_item_id'), value = parse(readItemField(row, 'ability_vfx_recipe'));
            if (typeof id === 'string' && value !== undefined) values.set(id, value);
        }
        return values;
    });
    const staticShellKeys = memoFromBaseRead(registries, 'pl-static-shell-keys', () => new Set(Object.values(registries).flatMap(registry => registry ? Object.entries(registry)
        .filter(([, row]) => row !== null && ['item_id', 'item_name', 'item_type'].every(key => readItemField(row, key) !== null))
        .map(([id, row]) => `${String(readItemField(row, 'item_type'))}:${id.toUpperCase()}`) : [])));
    return { calibration, vfx, staticShellKeys };
}
export function nativeSignatureTypes(source: Row, jsonSources: PlSignatureJsonSources): Row {
    const emptyDefenseSources: unknown[] = [];
    const permanentStatuses = new Map<string, Row>();
    const portraits = new Map<string, Row>();
    const choiceRows = new Map<string, { parent: string; rows: Row[]; uses: Map<string, number> }>();
    const choiceDefinitions = new Map<string, Row[]>();
    const player = readItemField(source, 'player_characters');
    const integerEssenceCaps = new Set(fromBaseRecord(player) ? ['body', 'mind', 'soul', 'spirit', 'fortune', 'luck'].filter(name =>
        phpJsonFloat(readItemField(player, `${name}_input_default`)) + phpJsonFloat(readItemField(player, `${name}_input_other`)) <= 5
        && phpJsonFloat(readItemField(player, `${name}_input_override`)) <= 0) : []);
    const spellLevel = fromBaseRecord(player) && readItemField(source, 'spell_casting_level') !== null;
    const encounter = readItemField(source, 'encounter_creatures');
    const hasManaPercent = fromBaseRecord(encounter) && phpJsonFloat(readItemField(encounter, 'spirit')) !== 0;
    function walk(value: unknown, path: string[] = [], shells = new Map<string, Row>(), enchanted = false, shareShells = true, staticType = ''): unknown {
        if (typeof value === 'number') {
            const key = path[path.length - 1], parent = path[path.length - 2];
            const layerNumber = parent === 'item_layer_types' && (staticType === 'size_type' && key === 'vitality_scale'
                || staticType === 'item_composition' && ['density_lb_ft3', 'cost_gp_ft3'].includes(key!) || enchanted && ['weight', 'item_charges'].includes(key!));
            const cost = enchanted && parent === 'cost_gp';
            const enchantment = parent === 'enchantment' && path[path.length - 3] === 'computed' && ['total_cost', 'item_charges'].includes(key!)
                || parent === 'cost_multipliers' && path[path.length - 3] === 'enchantment' && path[path.length - 4] === 'computed';
            const asset = parent === 'presentation' && ['min', 'max'].includes(key!) || parent === 'boundsM';
            const statusCoordinate = /^status_(?:origin_point_[xyz]|distance_point_[xyz]|aoe_distance(?:_depth|_height)?)$/.test(key ?? '');
            const inventoryTotal = path[path.length - 3] === 'computed' && (parent === 'cost' && ['total_gp', 'contents_total_gp'].includes(key!)
                || parent === 'weight' && ['total', 'held', 'gross'].includes(key!));
            // min(max(5, starting), total) retains the literal integer cap on
            // ties; ceil(half-levels / 2) makes the spell-level producer double.
            const playerNumber = path.length === 2 && /^(?:ancestral_|background_)?(?:body|mind|soul|spirit|fortune|luck)$/.test(path[0]!)
                    && !(value === 5 && integerEssenceCaps.has(path[0]!))
                || path.length === 3 && path[0] === 'derivative_speed_basis'
                || spellLevel && path.length === 2 && (path[0] === 'spell_casting_level'
                    || path[0] === 'player_characters' && key === 'spirit_input_default');
            const manaPercent = parent === 'encounter_creatures' && key === 'mana_percent' && hasManaPercent;
            return layerNumber || cost || enchantment || asset || statusCoordinate || inventoryTotal || playerNumber || manaPercent ? new PhpFloat(value) : value;
        }
        if (!value || typeof value !== 'object') return value;
        // Native defense rows initialize all_sources with the same PHP literal
        // empty array. Its igbinary identity survives until a source is appended.
        if (Object.keys(value).length === 0 && (path[path.length - 1] === 'all_sources' && path.includes('defenses')
            || path.length === 1 && ['_user_choice_nested_map', 'inventory'].includes(path[0]!))) return emptyDefenseSources;
        const row = value as Row, id = readItemField(row, 'item_id');
        const portrait = path.length >= 4 && path[path.length - 4] === 'encounter_creatures'
            && path[path.length - 3] === 'pictures'
            && ['encounter_creature_presentation_picture', 'encounter_creature_thumbnail_picture'].includes(path[path.length - 2]!)
            && path[path.length - 1] === '0' ? JSON.stringify([path.slice(0, -2), row]) : null;
        if (portrait && portraits.has(portrait)) return portraits.get(portrait)!;
        const nestedChoice = path.length === 3 && path[0] === '_user_choice_nested_map';
        const choiceIdentity = nestedChoice ? JSON.stringify(row) : null;
        let choiceMemo: { parent: string; rows: Row[]; uses: Map<string, number> } | undefined;
        if (choiceIdentity) {
            const parent = path[1]!;
            choiceMemo = choiceRows.get(choiceIdentity) ?? { parent, rows: [], uses: new Map() };
            choiceRows.set(choiceIdentity, choiceMemo);
            const occurrence = choiceMemo.uses.get(parent) ?? 0;
            choiceMemo.uses.set(parent, occurrence + 1);
            if (parent !== choiceMemo.parent && choiceMemo.rows[occurrence]) return choiceMemo.rows[occurrence]!;
        }
        if (fromBaseRecord(row.inventory) && readItemField(row.inventory, 'inventory_id') !== null) {
            enchanted = fromBaseRecord(row.computed) && fromBaseRecord(row.computed.enchantment)
                || Boolean(readItemField(row.inventory, 'enchantment_template_id'));
        }
        const permanent = (path.length === 2 && path[0] === 'status_effect_permanent'
            || path.length === 3 && path[0] === 'item_layer_types' && path[1] === 'status_effect_permanent')
            && fromBaseRecord(row.items) ? JSON.stringify(row) : null;
        if (permanent && permanentStatuses.has(permanent)) return permanentStatuses.get(permanent)!;
        if (fromBaseRecord(row.items)) {
            if ('computed' in row && path.length) shareShells = false;
            enchanted = fromBaseRecord(row.item_layer_types) && 'enchantment_template_applied' in row.item_layer_types
                || fromBaseRecord(row.inventory) && readItemField(row.inventory, 'inventory_static_display') === 1
                    && fromBaseRecord(row.computed) && fromBaseRecord(row.computed.enchantment);
        }
        const meshId = readItemField(row, 'id');
        const calibration = typeof meshId === 'string' && jsonSources.calibration.get(meshId);
        const vfx = typeof id === 'string' && jsonSources.vfx.get(id);
        const shell = typeof id === 'string' && fromBaseRecord(row.items)
            && Object.keys(row).every(key => ['items', 'item_layer_types'].includes(key));
        if (fromBaseRecord(row.items)) staticType = shell ? String(readItemField(row, 'item_type') ?? '') : '';
        // Native dedupe walks detach a nested definition's shells. Only the
        // current pre-dedupe expansion shares build_item_shell's memoized arrays.
        // Only the DB shell fallback is memoized. Static catalog bucketing
        // allocates a new PHP array each time, even for identical identities.
        const identity = shareShells && shell && !jsonSources.staticShellKeys?.has(`${staticType}:${id.toUpperCase()}`) ? JSON.stringify(row) : null;
        if (identity && shells.has(identity)) return shells.get(identity)!;
        const child = (key: string, value: unknown): unknown => walk(value, [...path, key], shells, enchanted, shareShells, staticType);
        const copy = Array.isArray(value) ? value.map((v, i) => child(String(i), v))
            : Object.fromEntries(Object.entries(row).map(([k, v]) => {
                // process_user_choices_layers reuses one pick under both parents
                // and on its host layer. Recreate only those producer aliases;
                // repeated identical choices keep separate occurrence identities.
                if (k === 'item_arr' && (nestedChoice || path.length === 2) && fromBaseRecord(v)
                    && readItemField(row, 'source') !== null) {
                    const pickSource = readItemField(row, 'source');
                    const pickType = nestedChoice ? String(readItemField(row, 'choice_type') ?? '').replace(/^user_choice_\d+_/, '') : path[0];
                    const pickLevel = ['at_will_spell', 'innate_spell_per_long_rest'].includes(pickType ?? '') ? null : readItemField(row, 'requires_level');
                    const token = JSON.stringify([pickSource, pickType, pickLevel, v]);
                    const reused = !nestedChoice && choiceDefinitions.get(token)?.shift();
                    if (reused) return [k, reused];
                    const item = child(k, v) as Row;
                    if (nestedChoice) {
                        const definitions = choiceDefinitions.get(token) ?? [];
                        definitions.push(item); choiceDefinitions.set(token, definitions);
                    }
                    return [k, item];
                }
                return [k, k === 'calibration' && calibration ? decodePhpJson(calibration)
                    : k === 'assets' && vfx && fromBaseRecord(v) && 'magic_vfx' in v
                        ? { ...child(k, v) as Row, magic_vfx: decodePhpJson(vfx) } : child(k, v)];
            }));
        if (identity) shells.set(identity, copy as Row);
        if (permanent) permanentStatuses.set(permanent, copy as Row);
        if (portrait) portraits.set(portrait, copy as Row);
        if (choiceMemo && choiceMemo.parent === path[1]) choiceMemo.rows.push(copy as Row);
        return copy;
    }
    return walk(source) as Row;
}

/** SHA-1 is a legacy cache identity, not an authentication primitive. */
export function sha1Bytes(input: readonly number[]): string {
    const bytes = [...input], length = BigInt(bytes.length) * 8n;
    bytes.push(0x80);
    while (bytes.length % 64 !== 56) bytes.push(0);
    for (let i = 7; i >= 0; i--) bytes.push(Number(length >> BigInt(i * 8) & 255n));
    const h = [0x67452301, 0xefcdab89, 0x98badcfe, 0x10325476, 0xc3d2e1f0];
    const rotate = (v: number, n: number): number => v << n | v >>> (32 - n);
    // One fixed schedule per hash instead of allocating an array per block.
    const w = new Int32Array(80);
    for (let offset = 0; offset < bytes.length; offset += 64) {
        for (let i = 0; i < 16; i++) {
            const j = offset + i * 4;
            w[i] = bytes[j]! << 24 | bytes[j + 1]! << 16 | bytes[j + 2]! << 8 | bytes[j + 3]!;
        }
        for (let i = 16; i < 80; i++) w[i] = rotate(w[i - 3]! ^ w[i - 8]! ^ w[i - 14]! ^ w[i - 16]!, 1);
        let [a, b, c, d, e] = h as [number, number, number, number, number];
        for (let i = 0; i < 80; i++) {
            const f = i < 20 ? b & c | ~b & d : i < 40 || i >= 60 ? b ^ c ^ d : b & c | b & d | c & d;
            const k = i < 20 ? 0x5a827999 : i < 40 ? 0x6ed9eba1 : i < 60 ? 0x8f1bbcdc : 0xca62c1d6;
            [a, b, c, d, e] = [(rotate(a, 5) + f + e + k + w[i]!) | 0, a, rotate(b, 30), c, d];
        }
        [a, b, c, d, e].forEach((value, i) => { h[i] = (h[i]! + value) | 0; });
    }
    return h.map(value => (value >>> 0).toString(16).padStart(8, '0')).join('');
}

export function compilePlSignatures(source: Row, metadata: PlSignatureMetadata, jsonSources: PlSignatureJsonSources): { pl_sig_b: string; pl_sig: string } {
    if (!['igbinary-v2', 'php-serialize'].includes(metadata.encoding) || typeof metadata.contract !== 'string'
        || !/^[0-9a-f]{8}$/.test(metadata.codeCrc)
        || metadata.definitionEpoch !== null && !Number.isSafeInteger(metadata.definitionEpoch))
        throw new TypeError('Invalid PL signature codec/contract/epoch');
    // The salts differ only in this root scalar. Type/alias reconstruction is
    // read-only and independent of that scalar; each codec owns fresh tables.
    const typed = nativeSignatureTypes(plSignatureProjection(source, 'plb:1'), jsonSources);
    const sign = (salt: string): string => {
        const projection = { ...typed, __pl_carry_salt: salt };
        return sha1Bytes(metadata.encoding === 'igbinary-v2' ? serializeIgbinary(projection) : utf8Bytes(serializePhpJson(projection)));
    };
    return { pl_sig_b: sign('plb:1'), pl_sig: sign(`plc:${metadata.contract}:${metadata.codeCrc}:${metadata.definitionEpoch ?? 'noepoch'}`) };
}
